import React, { useCallback, useState, useEffect } from 'react'
import { parsePhoneNumberWithError } from 'libphonenumber-js'

import { Button } from 'sputnik-ui'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Zoom,
  Typography,
  IconButton,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Formik, Form, useFormikContext } from 'formik'
import * as Yup from 'yup'
import FormField from 'components/atoms/FormField'
import { useDispatch, useSelector } from 'react-redux'
import { createPartner, verifyPhoneNumber } from 'store/partners/operations'
import styled from 'styled-components'
import { toggle } from 'utils/helpers'
import i18next from 'i18next'
import {
  IconColors,
  Message as MessageIcon,
  Check as CheckIcon,
} from 'sputnik-icons'
import { unwrapResult } from '@reduxjs/toolkit'
import ConfirmPhoneModal from '../ConfirmPhoneModal'

const BlueText = styled.span`
  color: ${(p) => p.theme.palette.blue.main};
`

const GreenText = styled.span`
  color: ${(p) => p.theme.palette.green.main};
`

function Transition(props) {
  return <Zoom direction="up" {...props} />
}

Yup.addMethod(Yup.string, 'phoneType', function (errorMessage) {
  return this.test(`test-phone-type`, errorMessage, function (value) {
    const { path, createError } = this
    try {
      return parsePhoneNumberWithError(value).isValid()
    } catch (error) {
      return createError({ path, message: errorMessage })
    }
  })
})

const Day = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${(p) => p.theme.sizes.borderRadius};
  display: grid;
  place-items: center;
  background: ${(p) =>
    p.selected
      ? p.theme.palette.secondary[500]
      : p.theme.palette.background.secondary};
  cursor: pointer;
  transition: all 200ms ease-in;
  margin: 0.325rem 0 0 0.325rem;
  font-size: 0.875rem;
  &:hover {
    background: ${(p) => p.theme.palette.secondary[300]};
  }
`

const days = [0, 1, 2, 3, 4, 5, 6]

const dayTypeCheck = (arr) => {
  if (arr === undefined || arr.length === 0) return [0, 1, 2, 3, 4]
  const type = arr.some((el) => typeof el === 'string')
  if (type) {
    return arr.map((el) => Number(el))
  }
  return arr
}

const dayToStr = (d) => {
  if (typeof d === 'number') {
    switch (d) {
      case 0:
        return i18next.t('time:days.short.mon')
      case 1:
        return i18next.t('time:days.short.tue')
      case 2:
        return i18next.t('time:days.short.wed')
      case 3:
        return i18next.t('time:days.short.thurs')
      case 4:
        return i18next.t('time:days.short.fri')
      case 5:
        return i18next.t('time:days.short.sat')
      case 6:
        return i18next.t('time:days.short.sun')
      default:
        return i18next.t('time:days.short.mon')
    }
  } else {
    throw new Error('day is NaN')
  }
}

const InputRow = styled.div`
  display: flex;
  margin-top: 1rem;
  gap: 10px;

  @media (max-width: 600px) {
    flex-direction: column; /* Stack vertically */
    gap: 8px;
    margin-top: 0.5rem;
  }
`

const PhoneVerificationField = ({
  name,
  label,
  placeholder,
  helperText,
  initialValue,
  confirmed,
  ...props
}) => {
  const { t } = useTranslation(['common', 'profile'])
  const dispatch = useDispatch()
  const [openVerifyPhoneNumberModal, setOpenVerifyPhoneNumberModal] =
    useState(false)
  const [phoneToConfirm, setPhoneToConfirm] = useState(null)
  const [phoneModified, setPhoneModified] = useState(false)
  const [phoneOverride, setPhoneOverride] = useState(false)
  const { values, setFieldValue, touched, errors } = useFormikContext()

  const handleConfirmPhoneModal = () => {
    setOpenVerifyPhoneNumberModal(!openVerifyPhoneNumberModal)
  }

  const checkPhoneNumber = (phone) => {
    dispatch(verifyPhoneNumber(phone))
      .then(unwrapResult)
      .then((payload) => {
        if (payload.confirmed === false) {
          setPhoneToConfirm(phone)
          handleConfirmPhoneModal()
        } else if (payload.confirmed && !payload.otp_sent) {
          setPhoneOverride(true)
        }
      })
  }

  return (
    <div>
      <InputRow>
        <FormField
          fullWidth
          label={label}
          name={name}
          type="phone"
          placeholder={placeholder}
          helperText={helperText}
          onChange={(e) => {
            setPhoneModified(e.target.value !== initialValue)
            setPhoneOverride(false)
            setFieldValue(name, e.target.value)
          }}
          {...props}
        />

        <IconButton
          onClick={() => checkPhoneNumber(values[name])}
          size="small"
          style={{ marginTop: '1rem' }}
          disabled={!phoneModified}
        >
          {phoneOverride || (confirmed && !phoneModified) ? (
            <div style={{ gap: '10px', display: 'flex' }}>
              <CheckIcon color={IconColors.green} />
              <GreenText style={{ cursor: 'pointer' }}>
                {t('profile:form.phone.verified')}
              </GreenText>
            </div>
          ) : (
            <div style={{ gap: '10px', display: 'flex' }}>
              <MessageIcon color={IconColors.blue} />
              <BlueText style={{ cursor: 'pointer' }}>
                {t('common:button.confirm_capital')}
              </BlueText>
            </div>
          )}
        </IconButton>
      </InputRow>
      {touched[name] && errors[name] && (
        <Typography
          style={{
            marginTop: '0.5rem',
            fontWeight: '600',
            fontFamily: 'Open Sans',
            fontSize: '12px',
            color: '#FF0E0E',
          }}
        >
          {errors[name]}
        </Typography>
      )}
      <ConfirmPhoneModal
        open={openVerifyPhoneNumberModal}
        Transition={Transition}
        handleClose={handleConfirmPhoneModal}
        phone={phoneToConfirm}
        onConfirm={setPhoneOverride}
      />
    </div>
  )
}

function ProfileModal({ open, handleClose }) {
  const { t } = useTranslation(['common', 'profile'])
  const schema = Yup.object().shape({
    organisation: Yup.string().required(t('common:field.error.required')),
    contact_phone: Yup.string()
      .phoneType(t('common:field.error.phone_invalid'))
      //  .matches(phoneRegex, t('common:field.error.phone_invalid'))
      .required(t('common:field.error.phone_invalid'))
      .nullable(),
    support_phone: Yup.string()
      .phoneType(t('common:field.error.phone_invalid'))
      // .matches(phoneRegex, t('common:field.error.phone_invalid'))
      .required(t('common:field.error.phone_invalid'))
      .nullable(),
    first_working_hour: Yup.string().required(t('common:field.error.required')),
    last_working_hour: Yup.string().required(t('common:field.error.required')),
    weekend_first_working_hour: Yup.string().required(
      t('common:field.error.required')
    ),
    weekend_last_working_hour: Yup.string().required(
      t('common:field.error.required')
    ),
    address: Yup.string().required(t('common:field.error.required')),
  })

  const [profileModalDismissed, setProfileModalDismissed] = useState(
    Boolean(sessionStorage.getItem('profileModalDismissed'))
  )

  const dispatch = useDispatch()
  const partner = useSelector(({ partners }) => partners.current)
  const isFilled = partner?.is_filled || true
  const isOpen = !profileModalDismissed && !isFilled

  const onClose = () => {
    sessionStorage.setItem('profileModalDismissed', true)
    setProfileModalDismissed(true)
  }

  const handleSubmit = useCallback(
    (values) => {
      dispatch(createPartner(values)).then(handleClose)
    },
    [dispatch, handleClose]
  )

  return (
    <Dialog
      open={open || isOpen}
      TransitionComponent={Transition}
      onClose={handleClose || onClose}
      disableBackdropClick
    >
      <DialogTitle>{t('profile:title')}</DialogTitle>
      <Formik
        initialValues={{
          ...partner,

          phone: '',
          first_working_hour: partner?.first_working_hour || '09:00',
          last_working_hour: partner?.last_working_hour || '18:00',
          weekend_first_working_hour:
            partner?.weekend_first_working_hour || '09:00',
          weekend_last_working_hour:
            partner?.weekend_last_working_hour || '18:00',
          working_days: dayTypeCheck(partner?.working_days),
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, setValues, isValid }) => (
          <Form>
            <DialogContent>
              <Typography>{t('profile:subheader')}</Typography>
              <FormField
                fullWidth
                label={t('profile:form.org')}
                name="organisation"
                style={{ marginTop: '1rem' }}
              />
              <FormField
                fullWidth
                label={t('profile:form.address')}
                name="address"
                style={{ marginTop: '1rem' }}
              />

              <PhoneVerificationField
                name="support_phone"
                label={t('profile:form.support_phone')}
                placeholder={t('profile:helper.example_phone')}
                helperText={t('profile:helper.support_phone')}
                initialValue={partner?.support_phone}
                confirmed={partner?.support_phone_confirmed}
              />

              <FormField
                style={{ marginTop: '1rem' }}
                fullWidth
                label={t('profile:form.additional_contacts')}
                name="additional_contacts"
                type="phone"
                helperText={t('profile:helper.additional_contacts')}
              />

              {/* 
              <div style={{ display: 'flex', marginTop: '1rem' }}>
                <FormField
                  fullWidth
                  label="Имя ответственного лица"
                  name="head_first_name"
                />
                <FormField
                  fullWidth
                  label="Фамилия ответственного лица"
                  name="head_last_name"
                  style={{ marginLeft: '1rem' }}
                />
              </div> */}
              <PhoneVerificationField
                name="contact_phone"
                label={t('profile:form.contact_phone')}
                placeholder={t('profile:helper.example_phone')}
                helperText={t('profile:helper.contact_phone')}
                initialValue={partner?.contact_phone}
                confirmed={partner?.contact_phone_confirmed}
              />
              <Typography style={{ marginTop: '1rem' }}>
                {t('profile:work_hours.title')}
              </Typography>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ width: '48%' }}>
                  <Typography color="textSecondary">
                    {t('profile:work_hours.weekdays')}
                  </Typography>
                  <div style={{ display: 'flex' }}>
                    <FormField
                      fullWidth
                      name="first_working_hour"
                      label={t('profile:form.work_from')}
                      style={{ marginRight: '.325rem' }}
                      type="time"
                    />
                    <FormField
                      fullWidth
                      name="last_working_hour"
                      label={t('profile:form.work_till')}
                      type="time"
                    />
                  </div>
                </div>
                <div style={{ width: '48%' }}>
                  <Typography color="textSecondary">
                    {t('profile:work_hours.weekend')}
                  </Typography>
                  <div style={{ display: 'flex' }}>
                    <FormField
                      fullWidth
                      name="weekend_first_working_hour"
                      label={t('profile:form.work_from')}
                      style={{ marginRight: '.325rem' }}
                      type="time"
                      disabled={
                        !values.working_days.includes(5) &&
                        !values.working_days.includes(6)
                      }
                    />
                    <FormField
                      fullWidth
                      name="weekend_last_working_hour"
                      label={t('profile:form.work_till')}
                      type="time"
                      disabled={
                        !values.working_days.includes(5) &&
                        !values.working_days.includes(6)
                      }
                    />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex' }}>
                {days.map((d) => (
                  <Day
                    key={d}
                    selected={values.working_days.includes(d)}
                    onClick={() => {
                      const newDays = [...values.working_days]
                      toggle(newDays, d)
                      setValues({
                        ...values,
                        working_days: newDays,
                      })
                    }}
                  >
                    {dayToStr(d)}
                  </Day>
                ))}
              </div>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose} rounded>
                {t('common:button.later')}
              </Button>
              <Button
                rounded
                primary
                variant="contained"
                type="submit"
                disabled={!isValid}
              >
                {t('common:button.confirm')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default ProfileModal
